import React, { useState, useContext, useReducer, useEffect } from "react";
import moment from "moment";
import styled from "styled-components";
import Select from "react-select";
import CreditCard from "@material-ui/icons/CreditCard";
import Button from "../../../../../../components/button";
import HelpTip from "../../../../../../components/help-tip";
import { updatePortalBilling, loadProfile } from "../../../../../../api";
import { AuthContext } from "../../../../../../auth-context";
import { client, hostedFields } from "braintree-web";
import { DashboardContext } from "../../../../dashboard-context";
import { Accordion } from "./accordion"
import config from "../../../../../../config";
import BraintreeLogo from "../../../../../../assets/payment-logos/braintree-badge-light.png";
import VisaPNG from "../../../../../../assets/payment-logos/visa-logo.png";
import MasterPNG from "../../../../../../assets/payment-logos/mastercard-logo.png";
import AmexPNG from "../../../../../../assets/payment-logos/amex-logo.png";
import JCBPNG from "../../../../../../assets/payment-logos/jcb-logo.png";
import DiscoverPNG from "../../../../../../assets/payment-logos/discover-logo.png";
import { countriesOptions } from "./countries"

const BRAINTREE_DROPIN = config.BRAINTREE_DROPIN;

const Container = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 0px 8px 0px #e1e2e6;
  width: 100%;
  padding: 24px;

  @media (max-width: 900px) {
    padding: 16px;
  }
`;

const Label = styled.label`
  color: #555;
  display: flex;
  align-items: center;
  font-family: "DM Sans", sans-serif;
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 24px;

  svg {
    fill: #555;
    height: auto;
    width: 36px;
    margin-right: 16px;
  }

  @media (max-width: 900px) {
    font-size: 1.25rem;
    margin-bottom: 16px;

    svg {
      width: 28px;
    }
  }
`;

const Info = styled.div`
  margin-bottom: 24px;

  p {
    color: #5a5a5a;
    font-size: 1rem;
  }

  span {
    font-family: ${(props) => props.theme.font.primary};
    font-size: 1.25rem;
    font-weight: 500;
  }
`;

const CardForm = styled.form`
  .field-container {
    label {
      color: #676767;
      display: block;
      font-family: ${(props) => props.theme.font.primary};
      font-size: 0.875rem;
      margin-bottom: 8px;

      @media (max-width: 900px) {
        font-size: 0.875rem;
      }
    }

    > div {
      border: 1px solid #dddee8;
      border-radius: 4px;
      color: #5a5a5a;
      font-family: ${(props) => props.theme.font.primary};
      font-size: 1.25rem;
      height: 50px;
      margin-bottom: 16px;
      padding: 12px;
      transition: 0.2s ease border, 0.2s ease box-shadow;
      width: 100%;

      &:hover {
        border: 1px solid #1890ff;
      }

      @media (max-width: 900px) {
        font-size: 1.125rem !important;
        padding: 8px;
      }
    }
  }

  .card-fields-row {
    display: flex;
    justify-content: space-between;

    > div {
      width: 49%;
    }
  }

  #card-number.braintree-hosted-fields-focused {
    border: 1px solid #1890ff;
    box-shadow: 0px 0px 0px 3px #b1dfffa6;
    outline: none;
  }

  #card-number.braintree-hosted-fields-invalid {
    border: 1px solid #f44336;
    box-shadow: 0px 0px 0px 3px #ffbbb6d1;
    outline: none;
  }

  #card-number.braintree-hosted-fields-valid {
    border: 1px solid #39ff81;
    box-shadow: 0px 0px 0px 3px #29c56238;
    outline: none;
  }

  #cvv.braintree-hosted-fields-focused {
    border: 1px solid #1890ff;
    box-shadow: 0px 0px 0px 3px #b1dfffa6;
    outline: none;
  }

  #cvv.braintree-hosted-fields-invalid {
    border: 1px solid #f44336;
    box-shadow: 0px 0px 0px 3px #ffbbb6d1;
    outline: none;
  }

  #cvv.braintree-hosted-fields-valid {
    border: 1px solid #39ff81;
    box-shadow: 0px 0px 0px 3px #29c56238;
    outline: none;
  }

  #expiration-date.braintree-hosted-fields-focused {
    border: 1px solid #1890ff;
    box-shadow: 0px 0px 0px 3px #b1dfffa6;
    outline: none;
  }

  #expiration-date.braintree-hosted-fields-invalid {
    border: 1px solid #f44336;
    box-shadow: 0px 0px 0px 3px #ffbbb6d1;
    outline: none;
  }

  #expiration-date.braintree-hosted-fields-valid {
    border: 1px solid #39ff81;
    box-shadow: 0px 0px 0px 3px #29c56238;
    outline: none;
  }

  #postal-code.braintree-hosted-fields-focused {
    border: 1px solid #1890ff;
    box-shadow: 0px 0px 0px 3px #b1dfffa6;
    outline: none;
  }

  #postal-code.braintree-hosted-fields-invalid {
    border: 1px solid #f44336;
    box-shadow: 0px 0px 0px 3px #ffbbb6d1;
    outline: none;
  }

  #postal-code.braintree-hosted-fields-valid {
    border: 1px solid #39ff81;
    box-shadow: 0px 0px 0px 3px #29c56238;
    outline: none;
  }

  .submit-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
    @media (max-width: 900px) {
      margin-top: 16px;
    }

    input {
      display: none;
    }
  }
`;

const UpdateBillingLoading = styled(Button)`
  margin-top: 24px;
  min-width: 180px;
`;

const CardBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 12px;

  img {
    display: block;
    height: 38px;
    margin: 0px 0px 8px 0px;
    width: auto;
    @media (max-width: 900px) {
      height: 30px;
    }
  }

  #braintree-logo {
    margin-left: 24px;
  }
`;

const AddressContainer = styled.div`
  margin-bottom: 48px;
`;

const AddressLine = styled.p`
  color: #2b3c4d !important;
  font-size: 1rem !important;
  margin-bottom: 0px !important;
`;

const SectionLabel = styled.p`
  color: #2b3c4d !important;
  font-size: 1.5rem;
  font-weight: bold;
`;

const customStyles = {
  container: (provided) => ({
    ...provided,
    margin: "0px 0px 16px 0px",
  }),
  control: (provided, state) => ({
    ...provided,
    background: "none",
    border: "1px solid #dddee8",
    borderRadius: "4px",
    height: '50px',
    color: state.isSelected ? "red" : "blue",
    boxShadow: "none",
    padding: "0px",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#5a5a5a",
    fontSize: "1.25rem",
    fontFamily: "DM Sans",
    fontWeight: "300",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0px 12px",
  }),
  option: (provided, state) => ({
    ...provided,
    background: state.isSelected ? "#e3e3e5" : "#FFF",
    color: "#5a5a5a",
  }),
};

// const initialState = {
//   firstName: "Jenna",
//   lastName: "Test",
//   company: "Braintree",
//   streetAddress: "1234 E Main St",
//   extendedAddress: "Suite 403",
//   locality: "Chicago",
//   region: "Illinois",
//   postalCode: "60607",
//   countryName: "United States of America"
// }

const initialState = {
  firstName: "",
  lastName: "",
  company: "",
  streetAddress: "",
  extendedAddress: "",
  locality: "",
  region: "",
  postalCode: "",
  countryName: ""
}

const formReducer = (state, action) => {
  switch (action.type) {
    case "field": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case "load" : {
      return {
        ...state,
        ...action.data
      };
    }
    case "reset": {
      return {
        firstName: "",
        lastName: "",
        company: "",
        streetAddress: "",
        extendedAddress: "",
        locality: "",
        region: "",
        postalCode: "",
        countryName: ""
      };
    }
    default:
      break;
  }

  return state;
};

const BillingInfo = (props) => {
  const { user, updateUser } = useContext(AuthContext);
  const { openSnackbar } = useContext(DashboardContext);
  const [state, dispatch] = useReducer(formReducer, initialState);
  const [isLoading, updateisLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  // const [showAddress, setShowAddress] = useState(false);
  const [nonce, setNonce] = useState('');

  useEffect(() => {
    if (nonce)
      submitForm();
  }, [nonce]);

  client.create(
    {
      authorization: BRAINTREE_DROPIN,
    },
    function (clientErr, clientInstance) {
      if (clientErr) {
        // Handle error in client creation
        return;
      }

      let options = {
        client: clientInstance,
        styles: {
          input: {
            color: "#5a5a5a",
            "font-size": "20px",
            "font-weight": "300",
          },
          "input.invalid": {
            color: "#F44336",
          },
          "input.valid": {
            color: "#088408",
          },
        },
        fields: {
          number: {
            selector: "#card-number",
            // placeholder: '4111 1111 1111 1111'
          },
          cvv: {
            selector: "#cvv",
            // placeholder: '123'
          },
          expirationDate: {
            selector: "#expiration-date",
            // placeholder: '10/2019'
          },
          // postalCode: {
          //   selector: "#postal-code",
          //   // placeholder: '11111'
          // },
        },
      };

      let form = document.querySelector("#my-sample-form");
      let submit = document.querySelector('button[type="submit"]');

      hostedFields.create(
        options,
        function (hostedFieldsErr, hostedFieldsInstance) {
          if (hostedFieldsErr) {
            // Handle error in Hosted Fields creation
            return;
          }

          // Use the Hosted Fields instance here to tokenize a card
          submit.removeAttribute("disabled");
          form.addEventListener(
            "submit",
            function (event) {
              event.preventDefault();

              hostedFieldsInstance.tokenize(function (tokenizeErr, payload) {
                if (tokenizeErr) {
                  openSnackbar(
                    "Please fill out all fields before submitting.",
                    true
                  );
                  console.error(tokenizeErr);
                  return;
                }
                setNonce(payload.nonce);
              });
            },
            false
          );
        }
      );
    }
  );

  const getPrice = (userCount) => {
    if (user.use_flat_rate) {
      return user.monthly_flat_rate;
    } else {
      return user.account_price * user.monthly_peak_basic;
    }
  };

  const submitForm = () => {
    if (
      state.firstName === "" ||
      state.lastName === "" ||
      state.streetAddress === "" ||
      state.locality === "" ||
      state.region === "" || 
      state.postalCode === "" ||
      state.countryName === ""
    ) {
      openSnackbar("Please fill out all fields before submitting.", true);
      return
    }
    
    if (nonce) {
      updateisLoading(true);

      updatePortalBilling(user.id, nonce, state)
        .then(() => {
          loadProfile(user.id)
            .then((res) => {
              if (res.data.profile.active === 1) {
                updateUser(res.data.profile);
              }
            })
            .catch((err) => {
              console.log(err);
            });
          openSnackbar(
            "Sucessfully updated your billing information.",
            false
          );
          updateisLoading(false);
        })
        .catch((err) => {
          openSnackbar(
            "There was a problem updating your billing information.",
            true
          );
          updateisLoading(false);
        });
    }
  }

  const selectedCountry = countriesOptions.filter((obj) => {
    return obj.value === state.countryName;
  });

  let price = getPrice();
  let credit = 0;
  let creditApplied = 0;
  if (price > 0 && user.balance < 0) {
    credit = Math.abs(user.balance)
    if (credit < price) {
      creditApplied = credit
      price = price - creditApplied
    } else {
      creditApplied = price - 1
      price = 1
    }
  }           

  return (
    <>
      <Container className={props.className}>
        <Label>
          <CreditCard /> Billing Information
        </Label>
        {user.balance < 0 &&
          <Info>
            <p>You have <span>${Math.abs(user.balance).toFixed(2)}</span> of credit. This will be applied on your upcoming bill.</p>
          </Info>
        }
        <Info>
          {user.use_flat_rate ? (
            <p>
              Your monthly flat rate is <span>${getPrice().toFixed(2)} per month</span>
            </p>
          ) : (
            <>
              <p>
                Your calculated billing for this month is currently <span>${price.toFixed(2)}</span> {user.balance < 0 && `($${creditApplied.toFixed(2)} credit applied)`}
              </p>
            </>
          )}
        </Info>
        <Info>
          <p>
            Your next billing date is on{" "}
            <span>{moment.utc(user.billing_date).format("MMM D, YYYY")}</span>
          </p>
        </Info>
        <Info>
          <p>
            Your current payment method:{" "}
            {user.defaultCard.last4 === "" ? (
              <span>None</span>
            ) : (
              <span>
                {user.defaultCard.cardType} ending in {user.defaultCard.last4}
              </span>
            )}
          </p>
        </Info>
        <AddressContainer>
          <p style={{fontSize: '1rem', color: '#2b3c4d'}}><b>Current Billing Address:</b></p>
          {user.billingAddress === '' || (!user.billingAddress.streetAddress) ?
            <AddressLine>None</AddressLine>
          :
            user.billingAddress.streetAddress &&
              <>
                <AddressLine>{user.billingAddress.firstName} {user.billingAddress.lastName}</AddressLine>
                <AddressLine>{user.billingAddress.company}</AddressLine>
                <AddressLine>{user.billingAddress.streetAddress}</AddressLine>
                <AddressLine>{user.billingAddress.extendedAddress}</AddressLine>
                <AddressLine>{user.billingAddress.locality}, {user.billingAddress.region} {user.billingAddress.postalCode}</AddressLine>
                <AddressLine>{user.billingAddress.countryName}</AddressLine>
              </>
          }
        </AddressContainer>
        <Accordion
          show={showPayment}
          setShow={(val) => setShowPayment(val)}          
          settingName="Update payment method"
          roundTop
        >
          <>
            <CardBadges>
              <img src={VisaPNG} alt="visa" />
              <img src={MasterPNG} alt="mastercard" />
              <img src={AmexPNG} alt="amex" />
              <img src={JCBPNG} alt="jcb" />
              <img src={DiscoverPNG} alt="discover" />
              <a
                href="https://www.braintreegateway.com/merchants/tm4gmb6qfhhnby8y/verified"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img id="braintree-logo" src={BraintreeLogo} alt="braintree logo" />
              </a>
            </CardBadges>
            <CardForm id="my-sample-form" autoComplete="off">
              <SectionLabel>
                Card information
              </SectionLabel>
              <div className="field-container">
                <label htmlFor="card-number">Card Number</label>
                <div id="card-number"></div>
              </div>

              <div className="card-fields-row">
                <div className="field-container">
                  <label htmlFor="cvv">CVV</label>
                  <div id="cvv"></div>
                </div>

                <div className="field-container">
                  <label htmlFor="expiration-date">Expiration Date</label>
                  <div id="expiration-date"></div>
                </div>
              </div>

              <div style={{height: '48px'}}></div>
              <SectionLabel>
                Billing address
              </SectionLabel>

              <>
                <Input
                  label="First name"
                  value={state.firstName}
                  onChange={(val) =>
                    dispatch({ type: "field", field: "firstName", value: val })
                  }
                />
                <Input
                  label="Last name"
                  value={state.lastName}
                  onChange={(val) =>
                    dispatch({ type: "field", field: "lastName", value: val })
                  }
                />
                <Input
                  label="Company"
                  value={state.company}
                  onChange={(val) =>
                    dispatch({ type: "field", field: "company", value: val })
                  }
                />
                <Input
                  label="Street Address"
                  value={state.streetAddress}
                  onChange={(val) =>
                    dispatch({ type: "field", field: "streetAddress", value: val })
                  }
                />
                <Input
                  label="Extended Address"
                  value={state.extendedAddress}
                  onChange={(val) =>
                    dispatch({ type: "field", field: "extendedAddress", value: val })
                  }
                />
                <Input
                  label="City (Locality)"
                  value={state.locality}
                  onChange={(val) =>
                    dispatch({ type: "field", field: "locality", value: val })
                  }
                />
                <Input
                  label="State/Province (Region)"
                  value={state.region}
                  onChange={(val) =>
                    dispatch({ type: "field", field: "region", value: val })
                  }
                />
                <Input
                  label="Postal Code"
                  value={state.postalCode}
                  onChange={(val) =>
                    dispatch({ type: "field", field: "postalCode", value: val })
                  }
                />
                <CustomLabel>Country</CustomLabel>
                <Select
                  value={selectedCountry}
                  onChange={(option) =>
                    dispatch({
                      type: "field",
                      field: "countryName",
                      value: option.value,
                    })
                  }
                  styles={customStyles}
                  options={countriesOptions}
                />
              </>

              <span className="submit-container">
                <UpdateBillingLoading
                  disabled={isLoading}
                  label="Update payment method"
                  type="submit"
                  isLoading={isLoading}
                />
                <input type="submit" value="Update Billing" disabled />
              </span>
            </CardForm>
          </>
        </Accordion>
      </Container>
    </>
  );
};

const CustomLabel = styled.label`
  color: #676767;
  display: block;
  font-family: ${(props) => props.theme.font.primary};
  font-size: 0.875rem;
  margin-bottom: 8px;

  @media (max-width: 900px) {
    font-size: 0.875rem;
  }
`;

const CustomInput = styled.div`
  input {
    border: 1px solid #dddee8;
    border-radius: 4px;
    color: #5a5a5a;
    font-family: ${(props) => props.theme.font.primary};
    font-size: 1.25rem;
    height: 50px;
    margin-bottom: 16px;
    padding: 12px;
    transition: 0.2s ease border, 0.2s ease box-shadow;
    width: 100%;

    &:hover {
      border: 1px solid #1890ff;
    }

    @media (max-width: 900px) {
      font-size: 1.125rem !important;
      padding: 8px;
    }
  }
`;

const Input = ({ label, value, onChange, placeholder }) => {
  return (
    <CustomInput>
      <CustomLabel>{label}</CustomLabel>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </CustomInput>
  );
};

export default BillingInfo;
